import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
 Server, Activity, Cpu, HardDrive, Network, 
 Users, Wifi, WifiOff, RefreshCw, Power, 
 AlertTriangle, CheckCircle, XCircle, Clock,
 Download, Upload, Signal
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import { MPanelOfflineBanner } from '../components/mpanel/MPanelOfflineBanner';
import { MPanelWANTraffic } from '../components/mpanel/MPanelWANTraffic';
import { MPanelStatsCards } from '../components/mpanel/MPanelStatsCards';
import { MPanelSystemInfo } from '../components/mpanel/MPanelSystemInfo';
import { formatBytes as formatBytesShared } from '../components/mpanel/utils';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const MPanel = () => {
 const { user } = useAuth();
 const { selectedBranch: globalSelectedBranch } = useApp();
 
 const [loading, setLoading] = useState(true);
 const [refreshing, setRefreshing] = useState(false);
 
 // Router selector
 const [availableRouters, setAvailableRouters] = useState([]);
 const [allRouters, setAllRouters] = useState([]); // Store all routers before filtering
 const [selectedRouter, setSelectedRouter] = useState(null);
 const [loadingRouters, setLoadingRouters] = useState(true);
 
 // Branch filter
 const [branches, setBranches] = useState([]);
 const [selectedBranch, setSelectedBranch] = useState('all'); // 'all' or branch_id
 
 // Data states
 const [systemResources, setSystemResources] = useState(null);
 const [activeSessions, setActiveSessions] = useState(null);
 const [dhcpLeases, setDhcpLeases] = useState([]);
 const [interfaces, setInterfaces] = useState([]);
 const [simpleQueues, setSimpleQueues] = useState([]);
 const [connectionStatus, setConnectionStatus] = useState('checking');
 
 // Tab state
 const [activeTab, setActiveTab] = useState('pppoe'); // pppoe or hotspot
 
 // Table controls for Active Sessions
 const [entriesPerPage, setEntriesPerPage] = useState(10);
 const [searchQuery, setSearchQuery] = useState('');
 const [currentPage, setCurrentPage] = useState(1);
 
 // Table controls for Network Interfaces
 const [interfacesEntriesPerPage, setInterfacesEntriesPerPage] = useState(10);
 const [interfacesSearchQuery, setInterfacesSearchQuery] = useState('');
 const [interfacesCurrentPage, setInterfacesCurrentPage] = useState(1);
 const [interfacesSortField, setInterfacesSortField] = useState('name');
 const [interfacesSortDir, setInterfacesSortDir] = useState('asc'); // asc | desc
 const [poolsSortField, setPoolsSortField] = useState('name');
 const [poolsSortDir, setPoolsSortDir] = useState('asc');
 
 // Table controls for IP Pools
 const [poolsEntriesPerPage, setPoolsEntriesPerPage] = useState(10);
 const [poolsSearchQuery, setPoolsSearchQuery] = useState('');
 const [poolsCurrentPage, setPoolsCurrentPage] = useState(1);
 
 // Custom Modal States
 const [confirmModal, setConfirmModal] = useState({
 isOpen: false,
 title: '',
 message: '',
 type: 'warning', // 'warning', 'danger', 'success', 'info'
 confirmText: 'OK',
 cancelText: 'Batal',
 onConfirm: null,
 showCancel: true
 });
 
 // Alert Modal (for results)
 const [alertModal, setAlertModal] = useState({
 isOpen: false,
 title: '',
 message: '',
 type: 'success' // 'success', 'error', 'info'
 });


 // Fetch branches
 useEffect(() => {
 const fetchBranches = async () => {
 try {
 const token = localStorage.getItem('token');
 const response = await axios.get(`${API_BASE_URL}/api/v2/branches`, {
 headers: { Authorization: `Bearer ${token}` }
 });
 
 if (response.data.success) {
 setBranches(response.data.data || []);
 }
 } catch (error) {
 console.error('Error fetching branches:', error);
 }
 };
 
 fetchBranches();
 }, []);
 
 // Fetch available routers - react to global branch filter
 useEffect(() => {
 const fetchRouters = async () => {
 try {
 setLoadingRouters(true);
 const token = localStorage.getItem('token');
 
 // Build URL with branch filter from global context
 let url = `${API_BASE_URL}/api/mpanel/available-routers`;
 if (globalSelectedBranch && globalSelectedBranch !== 'all') {
 url += `?branch_id=${globalSelectedBranch}`;
 }
 
 const response = await axios.get(url, {
 headers: { Authorization: `Bearer ${token}` }
 });
 
 if (response.data.success) {
 if (response.data.data.length > 0) {
 setAllRouters(response.data.data); // Store all routers
 setAvailableRouters(response.data.data);
 
 // Get saved router from localStorage or use first available
 const savedRouterId = localStorage.getItem('mpanel_selected_router');
 const savedRouter = response.data.data.find(r => r.id === savedRouterId || r.uuid === savedRouterId);
 
 if (savedRouter) {
 setSelectedRouter(savedRouter);
 } else {
 // Select first online router or just first router
 const firstOnline = response.data.data.find(r => r.status === 'online' || r.is_active);
 setSelectedRouter(firstOnline || response.data.data[0]);
 }
 } else {
 // No routers available
 setAllRouters([]);
 setAvailableRouters([]);
 setSelectedRouter(null);
 }
 }
 } catch (error) {
 console.error('Error fetching routers:', error);
 toast.error('Gagal memuat daftar router');
 } finally {
 setLoadingRouters(false);
 }
 };
 
 fetchRouters();
 }, [globalSelectedBranch]);

 // Handle branch filter change
 const handleBranchChange = (branchId) => {
 setSelectedBranch(branchId);
 
 // Filter routers by branch
 let filteredRouters = allRouters;
 if (branchId !== 'all') {
 filteredRouters = allRouters.filter(r => r.branch_id === branchId);
 }
 
 setAvailableRouters(filteredRouters);
 
 // Reset selected router if current one is not in filtered list
 if (selectedRouter && !filteredRouters.find(r => r.id === selectedRouter.id)) {
 const firstOnline = filteredRouters.find(r => r.status === 'online');
 setSelectedRouter(firstOnline || filteredRouters[0] || null);
 }
 };
 
 // Fetch all data
 const fetchAllData = useCallback(async (showLoading = true) => {
 if (!selectedRouter) return; // Don't fetch if no router selected
 
 if (showLoading) setLoading(true);
 else setRefreshing(true);
 
 try {
 const token = localStorage.getItem('token');
 const headers = { Authorization: `Bearer ${token}` };
 const routerParam = `?router_id=${selectedRouter.id}`;
 
 // Check connection mode
 const connectionMode = selectedRouter.connection_mode || 'api_only';

 // For RADIUS Only mode, fetch data from RADIUS endpoints instead of API
 if (connectionMode === 'radius_only') {
 // Fetch RADIUS-based data
 const [radiusStatsRes, radiusSessionsRes] = await Promise.all([
 axios.get(`${API_BASE_URL}/api/radius/stats`, { headers }).catch(() => ({ data: { success: false } })),
 axios.get(`${API_BASE_URL}/api/radius/active-sessions${routerParam}`, { headers }).catch(() => ({ data: { success: false } }))
 ]);

 // Set basic system resources from RADIUS data
 if (radiusStatsRes.data.success) {
 setSystemResources({
 platform: 'RADIUS Mode',
 version: 'FreeRADIUS',
 uptime: '-',
 cpu_load: '-',
 free_memory: '-',
 total_memory: '-',
 free_hdd: '-',
 total_hdd: '-',
 board_name: selectedRouter.router_name,
 architecture_name: 'RADIUS Only'
 });
 } else {
 setSystemResources({
 platform: 'RADIUS Mode',
 version: 'FreeRADIUS',
 uptime: 'N/A',
 cpu_load: 'N/A',
 free_memory: 'N/A',
 total_memory: 'N/A',
 board_name: selectedRouter.router_name,
 architecture_name: 'RADIUS Only - No API'
 });
 }

 if (radiusSessionsRes.data.success) {
 // Transform RADIUS sessions to match MPANEL format
 const radiusSessions = radiusSessionsRes.data.data || [];
 setActiveSessions({
 pppoe_active: radiusSessions.filter(s => s.service_type === 'Framed-User').length,
 hotspot_active: radiusSessions.filter(s => s.service_type === 'Login-User').length,
 pppoe_sessions: radiusSessions.filter(s => s.service_type === 'Framed-User').map(s => ({
 name: s.username,
 address: s.framedipaddress || s.ip_address || '-',
 uptime: s.uptime || s.acctsessiontime || '-',
 caller_id: s.callingstationid || '-',
 service: 'pppoe'
 })),
 hotspot_sessions: radiusSessions.filter(s => s.service_type === 'Login-User').map(s => ({
 user: s.username,
 address: s.framedipaddress || s.ip_address || '-',
 uptime: s.uptime || s.acctsessiontime || '-',
 mac_address: s.callingstationid || '-'
 }))
 });
 } else {
 setActiveSessions({
 pppoe_active: 0,
 hotspot_active: 0,
 pppoe_sessions: [],
 hotspot_sessions: []
 });
 }

 // Clear API-specific data for RADIUS mode
 setInterfaces([]);
 setSimpleQueues([]);
 setDhcpLeases([]);
 setConnectionStatus('configured');
 
 } else {
 // API Only or Hybrid mode - use normal API endpoints
 const [resourcesRes, sessionsRes, interfacesRes, poolsRes] = await Promise.all([
 axios.get(`${API_BASE_URL}/api/mpanel/system-resources${routerParam}`, { headers }),
 axios.get(`${API_BASE_URL}/api/mpanel/active-sessions${routerParam}`, { headers }),
 axios.get(`${API_BASE_URL}/api/mpanel/interfaces${routerParam}`, { headers }),
 axios.get(`${API_BASE_URL}/api/mpanel/simple-queues${routerParam}`, { headers })
 ]);

 if (resourcesRes.data.success) {
 setSystemResources(resourcesRes.data.data);
 }

 if (sessionsRes.data.success) {
 setActiveSessions(sessionsRes.data.data);
 }

 if (interfacesRes.data.success) {
 setInterfaces(interfacesRes.data.data);
 }

 if (poolsRes.data.success) {
 setSimpleQueues(poolsRes.data.data);
 }

 // Load DHCP in background (slower, non-critical)
 axios.get(`${API_BASE_URL}/api/mpanel/dhcp-leases${routerParam}`, { 
 headers,
 timeout: 10000 // 10 second timeout
 })
 .then(dhcpRes => {
 if (dhcpRes.data.success) {
 setDhcpLeases(dhcpRes.data.data || []);
 }
 })
 .catch(err => {
 console.warn('DHCP leases failed to load:', err.message);
 setDhcpLeases([]); // Set empty array on failure
 });

 // Update connection status based on data fetch success.
 // When offline, also clear stale data so UI never shows misleading info.
 if (resourcesRes.data.success) {
 setConnectionStatus('online');
 } else {
 setConnectionStatus('offline');
 setSystemResources(null);
 setActiveSessions(null);
 setInterfaces([]);
 setDhcpLeases([]);
 setSimpleQueues([]);
 }
 }

 } catch (error) {
 console.error('Error fetching MPANEL data:', error);
 setConnectionStatus('error');
 // Clear stale data on any fetch error too
 setSystemResources(null);
 setActiveSessions(null);
 setInterfaces([]);
 setDhcpLeases([]);
 setSimpleQueues([]);
 } finally {
 setLoading(false);
 setRefreshing(false);
 }
 }, [selectedRouter]);

 // Initial load - fetch when router is selected
 useEffect(() => {
 if (!loadingRouters) {
 if (selectedRouter) {
 fetchAllData();
 } else {
 // No router selected, stop loading
 setLoading(false);
 }
 }
 }, [selectedRouter, loadingRouters, fetchAllData]);


 // Handle router selection change
 const handleRouterChange = (routerId) => {
 // Convert to number for comparison since id might be number but value from select is string
 const routerIdNum = parseInt(routerId, 10);
 const router = availableRouters.find(r => r.id === routerIdNum || r.id === routerId || String(r.id) === String(routerId));
 if (router) {
 setSelectedRouter(router);
 localStorage.setItem('mpanel_selected_router', String(router.id));
 // Data will be fetched automatically via useEffect
 }
 };

 // Format bytes to readable size
 const formatBytes = (bytes) => {
 if (bytes === 0) return '0 B';
 const k = 1024;
 const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
 const i = Math.floor(Math.log(bytes) / Math.log(k));
 return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
 };

 // Show custom alert modal
 const showAlert = (title, message, type = 'success') => {
 setAlertModal({
 isOpen: true,
 title,
 message,
 type
 });
 };

 // Show custom confirm modal
 const showConfirm = (options) => {
 return new Promise((resolve) => {
 setConfirmModal({
 isOpen: true,
 title: options.title || 'Konfirmasi',
 message: options.message || '',
 type: options.type || 'warning',
 confirmText: options.confirmText || 'OK',
 cancelText: options.cancelText || 'Batal',
 showCancel: options.showCancel !== false,
 onConfirm: () => {
 setConfirmModal(prev => ({ ...prev, isOpen: false }));
 resolve(true);
 },
 onCancel: () => {
 setConfirmModal(prev => ({ ...prev, isOpen: false }));
 resolve(false);
 }
 });
 });
 };

 // Disconnect all sessions
 const handleDisconnectAll = async () => {
 const confirmed = await showConfirm({
 title: '⚠️ Peringatan',
 message: 'Anda akan memutuskan SEMUA koneksi aktif (PPPoE + Hotspot).\n\nSemua user akan terputus!',
 type: 'warning',
 confirmText: 'Ya, Putuskan Semua',
 cancelText: 'Batal'
 });
 
 if (!confirmed) return;

 try {
 const token = localStorage.getItem('token');
 const response = await axios.post(
 `${API_BASE_URL}/api/mpanel/disconnect-all`,
 {},
 { headers: { Authorization: `Bearer ${token}` } }
 );

 if (response.data.success) {
 showAlert(
 '✅ Berhasil!',
 `PPPoE: ${response.data.pppoe_disconnected} session terputus\nHotspot: ${response.data.hotspot_disconnected} session terputus\nTotal: ${response.data.total_disconnected} session`,
 'success'
 );
 fetchAllData(false);
 } else {
 showAlert('❌ Gagal', response.data.message, 'error');
 }
 } catch (error) {
 console.error('Error disconnecting all:', error);
 showAlert('❌ Error', error.message, 'error');
 }
 };

 // Reboot router - Step 1
 const handleReboot = async () => {
 const confirmed1 = await showConfirm({
 title: '🚨 PERINGATAN KRITIS!',
 message: 'Anda akan REBOOT router MikroTik.\n\nSemua koneksi akan terputus dan router akan offline beberapa menit!',
 type: 'danger',
 confirmText: 'Ya, Saya Yakin',
 cancelText: 'Batal'
 });
 
 if (!confirmed1) return;

 // Second confirmation
 const confirmed2 = await showConfirm({
 title: '⚠️ KONFIRMASI TERAKHIR',
 message: 'Ini akan menyebabkan DOWNTIME!\n\nPastikan Anda sudah mengecek semua.',
 type: 'danger',
 confirmText: 'LANJUTKAN REBOOT',
 cancelText: 'Batalkan'
 });
 
 if (!confirmed2) return;

 try {
 const token = localStorage.getItem('token');
 const response = await axios.post(
 `${API_BASE_URL}/api/mpanel/reboot-router`,
 { router_id: selectedRouter?.id || selectedRouter?.uuid },
 { headers: { Authorization: `Bearer ${token}` } }
 );

 if (response.data.success) {
 showAlert(
 '✅ Perintah Reboot Terkirim!',
 `Router ${selectedRouter?.name} akan restart dalam beberapa detik.\n\nHarap tunggu ~2-3 menit untuk router kembali online.`,
 'success'
 );
 setConnectionStatus('rebooting');
 } else {
 showAlert('❌ Gagal Reboot', response.data.message, 'error');
 }
 } catch (error) {
 console.error('Error rebooting router:', error);
 showAlert('❌ Error', error.message, 'error');
 }
 };


 // Filter and paginate sessions
 const getFilteredSessions = () => {
 if (!activeSessions) return { data: [], total: 0 };
 
 const sessions = activeTab === 'pppoe' 
 ? activeSessions.pppoe_sessions || []
 : activeSessions.hotspot_sessions || [];
 
 // Filter by search query
 const filtered = sessions.filter(session => {
 if (!searchQuery) return true;
 
 const query = searchQuery.toLowerCase();
 const username = (session.name || session.user || '').toLowerCase();
 const ip = (session.address || '').toLowerCase();
 const callerId = (session.caller_id || session.mac_address || '').toLowerCase();
 
 return username.includes(query) || ip.includes(query) || callerId.includes(query);
 });
 
 // Paginate
 const startIndex = (currentPage - 1) * entriesPerPage;
 const endIndex = startIndex + entriesPerPage;
 const paginatedData = filtered.slice(startIndex, endIndex);
 
 return {
 data: paginatedData,
 total: filtered.length,
 totalPages: Math.ceil(filtered.length / entriesPerPage),
 startIndex: filtered.length > 0 ? startIndex + 1 : 0,
 endIndex: Math.min(endIndex, filtered.length)
 };
 };


 // Sort helpers
 const toggleSort = (current, dir, setCur, setDir, key) => {
 if (current === key) {
 setDir(dir === 'asc' ? 'desc' : 'asc');
 } else {
 setCur(key);
 setDir('asc');
 }
 };
 const sortIcon = (active, dir) => active ? (dir === 'asc' ? ' ▲' : ' ▼') : '';

 // Filter and paginate interfaces
 const getFilteredInterfaces = () => {
 if (!interfaces || interfaces.length === 0) return { data: [], total: 0 };
 
 // Filter by search query
 const filtered = interfaces.filter(iface => {
 if (!interfacesSearchQuery) return true;
 
 const query = interfacesSearchQuery.toLowerCase();
 const name = (iface.name || '').toLowerCase();
 const mac = (iface.mac_address || '').toLowerCase();
 const comment = (iface.comment || '').toLowerCase();
 
 return name.includes(query) || mac.includes(query) || comment.includes(query);
 });
 
 // Sort
 const numericFields = new Set(['rx_byte', 'tx_byte']);
 const sortKey = interfacesSortField;
 const dir = interfacesSortDir === 'asc' ? 1 : -1;
 filtered.sort((a, b) => {
 let va = a[sortKey];
 let vb = b[sortKey];
 if (sortKey === 'status') {
 va = (a.running ? 1 : 0) - (a.disabled ? 2 : 0);
 vb = (b.running ? 1 : 0) - (b.disabled ? 2 : 0);
 }
 if (numericFields.has(sortKey)) {
 va = Number(va) || 0; vb = Number(vb) || 0;
 } else {
 va = String(va ?? '').toLowerCase();
 vb = String(vb ?? '').toLowerCase();
 }
 if (va < vb) return -1 * dir;
 if (va > vb) return 1 * dir;
 return 0;
 });
 
 // Paginate
 const startIndex = (interfacesCurrentPage - 1) * interfacesEntriesPerPage;
 const endIndex = startIndex + interfacesEntriesPerPage;
 const paginatedData = filtered.slice(startIndex, endIndex);
 
 return {
 data: paginatedData,
 total: filtered.length,
 totalPages: Math.ceil(filtered.length / interfacesEntriesPerPage),
 startIndex: filtered.length > 0 ? startIndex + 1 : 0,
 endIndex: Math.min(endIndex, filtered.length)
 };
 };

 // Filter and paginate Simple Queues
 const getFilteredPools = () => {
 if (!simpleQueues || simpleQueues.length === 0) return { data: [], total: 0 };
 
 // Filter by search query
 const filtered = simpleQueues.filter(q => {
 if (!poolsSearchQuery) return true;
 
 const query = poolsSearchQuery.toLowerCase();
 const name = (q.name || '').toLowerCase();
 const target = (q.target || '').toLowerCase();
 const maxLimit = (q.max_limit || '').toLowerCase();
 
 return name.includes(query) || target.includes(query) || maxLimit.includes(query);
 });
 
 // Sort
 const sortKey2 = poolsSortField;
 const dir2 = poolsSortDir === 'asc' ? 1 : -1;
 filtered.sort((a, b) => {
 const va = String(a[sortKey2] ?? '').toLowerCase();
 const vb = String(b[sortKey2] ?? '').toLowerCase();
 if (va < vb) return -1 * dir2;
 if (va > vb) return 1 * dir2;
 return 0;
 });
 
 // Paginate
 const startIndex = (poolsCurrentPage - 1) * poolsEntriesPerPage;
 const endIndex = startIndex + poolsEntriesPerPage;
 const paginatedData = filtered.slice(startIndex, endIndex);
 
 return {
 data: paginatedData,
 total: filtered.length,
 totalPages: Math.ceil(filtered.length / poolsEntriesPerPage),
 startIndex: filtered.length > 0 ? startIndex + 1 : 0,
 endIndex: Math.min(endIndex, filtered.length)
 };
 };



 if (loading) {
 return (
 <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-3 sm:p-6">
 <div className="flex items-center justify-center h-64">
 <div className="flex flex-col items-center gap-3">
 <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
 <p className="text-gray-600 dark:text-gray-400">Loading MPANEL...</p>
 </div>
 </div>
 </div>
 );
 }

 return (
 <div className="p-3 sm:p-6 space-y-4 sm:space-y-6 ">
 {/* Header */}
 <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
 <div>
 <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2 sm:gap-3">
 <Server className="w-6 h-6 sm:w-7 sm:h-7 text-blue-600 dark:text-blue-400" />
 MPANEL
 <span className="text-sm sm:text-lg font-normal text-gray-500 dark:text-gray-400">MikroTik Panel</span>
 </h1>
 <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
 Real-time monitoring & management untuk MikroTik Router
 </p>
 </div>

 {/* Connection Status Badge */}
 <div className="flex flex-wrap items-center gap-2 sm:gap-3">
 
 {/* Router Selector */}
 {!loadingRouters && availableRouters.length > 0 && selectedRouter && (
 <div className="flex items-center gap-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg px-2 sm:px-3 py-2 w-full sm:w-auto">
 <Server className="w-4 h-4 text-gray-500 flex-shrink-0 dark:text-slate-400" />
 <select
 value={String(selectedRouter.id)}
 onChange={(e) => handleRouterChange(e.target.value)}
 className="bg-transparent border-none text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-0 cursor-pointer flex-1 min-w-0 truncate"
 >
 {availableRouters.map(router => (
 <option key={router.id} value={String(router.id)}>
 {router.name} ({router.host})
 </option>
 ))}
 </select>
 {/* Connection Mode Badge */}
 {selectedRouter.connection_mode === 'radius_only' && (
 <span className="px-1.5 sm:px-2 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400 rounded text-[10px] sm:text-xs font-medium flex-shrink-0">
 📊 RADIUS
 </span>
 )}
 {selectedRouter.connection_mode === 'hybrid' && (
 <span className="px-1.5 sm:px-2 py-0.5 bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-400 rounded text-[10px] sm:text-xs font-medium flex-shrink-0">
 ⭐ Hybrid
 </span>
 )}
 {(!selectedRouter.connection_mode || selectedRouter.connection_mode === 'api_only') && (
 <span className="px-1.5 sm:px-2 py-0.5 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400 rounded text-[10px] sm:text-xs font-medium flex-shrink-0">
 🔌 API
 </span>
 )}
 </div>
 )}
 
 {loadingRouters && (
 <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-slate-700 rounded-lg">
 <Activity className="w-4 h-4 text-gray-500 animate-spin dark:text-slate-400" />
 <span className="text-sm text-gray-600 dark:text-gray-300">Loading routers...</span>
 </div>
 )}
 
 {!loadingRouters && availableRouters.length === 0 && (
 <div className="flex items-center gap-2 px-3 py-2 bg-orange-100 dark:bg-orange-900/40 rounded-lg">
 <AlertTriangle className="w-4 h-4 text-orange-600 dark:text-orange-400" />
 <span className="text-sm text-orange-700 dark:text-orange-400">No active routers</span>
 </div>
 )}
 
 {connectionStatus === 'online' && (
 <span className="flex items-center gap-2 px-3 py-1.5 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400 rounded-full text-sm font-medium">
 <CheckCircle className="w-4 h-4" />
 Online
 </span>
 )}
 {connectionStatus === 'offline' && (
 <span className="flex items-center gap-2 px-3 py-1.5 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400 rounded-full text-sm font-medium">
 <XCircle className="w-4 h-4" />
 Offline
 </span>
 )}
 {connectionStatus === 'rebooting' && (
 <span className="flex items-center gap-2 px-3 py-1.5 bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-400 rounded-full text-sm font-medium">
 <Power className="w-4 h-4 animate-spin" />
 Rebooting...
 </span>
 )}
 </div>
 </div>

 {/* No Routers Message */}
 {!loadingRouters && availableRouters.length === 0 && (
 <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-12">
 <div className="flex flex-col items-center justify-center text-center">
 <Server className="w-16 h-16 text-gray-300 mb-4" />
 <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Tidak Ada Router Tersedia</h3>
 <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 max-w-md">
 Tidak ada router yang tersedia untuk monitoring saat ini.
 </p>
 <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 max-w-2xl text-left">
 <h4 className="font-semibold text-blue-900 dark:text-blue-200 mb-2">Kemungkinan penyebab:</h4>
 <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1 list-disc list-inside">
 <li>Router belum dikonfigurasi di halaman <strong>Konfigurasi MikroTik</strong></li>
 <li>IP router belum ditambahkan ke <strong>IP Whitelist</strong></li>
 <li>IP di whitelist dalam status <strong>non-aktif</strong></li>
 <li>Router tidak matching dengan tenant/cabang Anda</li>
 </ul>
 </div>
 <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
 Hubungi administrator jika Anda yakin router sudah dikonfigurasi dengan benar.
 </p>
 </div>
 </div>
 )}

 {/* Quick Actions Bar */}
 {selectedRouter && (
 <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-3 sm:p-4">
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
 <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
 {/* Manual Refresh Button */}
 <button
 onClick={() => fetchAllData(false)}
 disabled={refreshing}
 className="flex items-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-all shadow-sm w-full sm:w-auto justify-center sm:justify-start"
 >
 <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
 <span className="font-medium">{refreshing ? 'Refreshing...' : 'Refresh Data'}</span>
 </button>
 
 <div className="hidden sm:block text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-slate-700 px-3 py-1.5 rounded-md">
 💡 Klik tombol Refresh untuk mendapatkan data terbaru dari router
 </div>
 </div>

 <div className="flex items-center gap-2">
 {/* Disconnect All */}
 <button
 onClick={handleDisconnectAll}
 disabled={connectionStatus !== 'online'}
 title={connectionStatus !== 'online' ? 'Router offline - aksi dinonaktifkan' : 'Disconnect semua sesi aktif'}
 className="flex-1 sm:flex-none flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 text-xs sm:text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-orange-500"
 >
 <WifiOff className="w-3 h-3 sm:w-4 sm:h-4" />
 <span className="hidden sm:inline">Disconnect All</span>
 <span className="sm:hidden">Disconnect</span>
 </button>

 {/* Reboot Router */}
 <button
 onClick={handleReboot}
 disabled={connectionStatus !== 'online'}
 title={connectionStatus !== 'online' ? 'Router offline - aksi dinonaktifkan' : 'Reboot router'}
 className="flex-1 sm:flex-none flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 text-xs sm:text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-red-500"
 >
 <Power className="w-3 h-3 sm:w-4 sm:h-4" />
 <span className="hidden sm:inline">Reboot Router</span>
 <span className="sm:hidden">Reboot</span>
 </button>
 </div>
 </div>
 </div>
 )}

 {/* Currently Monitoring Info */}
 {selectedRouter && (
 <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-2 sm:p-3">
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
 <div className="flex items-center gap-2">
 <div className="p-1.5 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex-shrink-0">
 <Server className="w-4 h-4 text-blue-600 dark:text-blue-400" />
 </div>
 <div className="min-w-0">
 <h3 className="text-xs font-semibold text-gray-900 dark:text-white">Currently Monitoring</h3>
 <div className="flex flex-wrap items-center gap-1 sm:gap-2 mt-0.5">
 <span className="text-xs sm:text-sm font-bold text-blue-600 truncate dark:text-blue-400">{selectedRouter.router_name}</span>
 <span className="text-xs text-gray-400 hidden sm:inline">•</span>
 <span className="text-[10px] sm:text-xs text-gray-600 font-mono dark:text-slate-300">{selectedRouter.host}:{selectedRouter.port}</span>
 {selectedRouter.location && (
 <>
 <span className="text-xs text-gray-400 hidden sm:inline">•</span>
 <span className="text-[10px] sm:text-xs text-gray-600 dark:text-gray-300">{selectedRouter.location}</span>
 </>
 )}
 </div>
 </div>
 </div>
 <div className="flex items-center gap-2">
 {connectionStatus === 'online' && (
 <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-500 text-white rounded-full text-xs font-medium">
 <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse dark:bg-slate-800"></div>
 Connected
 </div>
 )}
 </div>
 </div>
 </div>
 )}

 <MPanelStatsCards
 visible={!!selectedRouter}
 activeSessions={activeSessions}
 dhcpLeases={dhcpLeases}
 />

 <MPanelOfflineBanner visible={selectedRouter && connectionStatus === 'offline'} />
 
 <MPanelSystemInfo
 visible={connectionStatus !== 'offline'}
 systemResources={systemResources}
 />

 <MPanelWANTraffic
 visible={connectionStatus !== 'offline'}
 interfaces={interfaces}
 formatBytes={formatBytes}
 />

 {/* Active Sessions - hidden when offline */}
 {connectionStatus !== 'offline' && (
 <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
 <div className="flex items-center justify-between mb-4">
 <div className="flex items-center gap-2">
 <Users className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
 <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Active Sessions</h2>
 </div>
 {activeSessions && (
 <div className="flex items-center gap-4">
 <span className="text-sm text-gray-600 dark:text-gray-300">
 Total: <span className="font-semibold text-gray-900 dark:text-white">{activeSessions.total_sessions}</span>
 </span>
 <span className="text-sm text-gray-600 dark:text-gray-300">
 PPPoE: <span className="font-semibold text-blue-600 dark:text-blue-400">{activeSessions.total_pppoe}</span>
 </span>
 <span className="text-sm text-gray-600 dark:text-gray-300">
 Hotspot: <span className="font-semibold text-purple-600 dark:text-purple-400">{activeSessions.total_hotspot}</span>
 </span>
 </div>
 )}
 </div>

 {/* Tabs */}
 <div className="flex gap-2 mb-4 border-b border-gray-200 dark:border-slate-700">
 <button
 onClick={() => setActiveTab('pppoe')}
 className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
 activeTab === 'pppoe'
 ? 'border-blue-500 text-blue-600'
 : 'border-transparent text-gray-500 hover:text-gray-700'
 }`}
 >
 PPPoE ({activeSessions?.total_pppoe || 0})
 </button>
 <button
 onClick={() => setActiveTab('hotspot')}
 className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
 activeTab === 'hotspot'
 ? 'border-purple-500 text-purple-600'
 : 'border-transparent text-gray-500 hover:text-gray-700'
 }`}
 >
 Hotspot ({activeSessions?.total_hotspot || 0})
 </button>
 </div>

 {/* Table Controls - Search & Entries */}
 <div className="flex items-center justify-between mb-4 gap-4">
 <div className="flex items-center gap-2">
 <label className="text-sm text-gray-600 dark:text-gray-300">Show</label>
 <select
 value={entriesPerPage}
 onChange={(e) => {
 setEntriesPerPage(Number(e.target.value));
 setCurrentPage(1);
 }}
 className="px-3 py-1.5 text-sm border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-700 dark:text-white"
 >
 <option value={10}>10</option>
 <option value={25}>25</option>
 <option value={50}>50</option>
 <option value={100}>100</option>
 </select>
 <label className="text-sm text-gray-600 dark:text-gray-300">entries</label>
 </div>

 <div className="flex-1 max-w-md">
 <div className="relative">
 <input
 type="text"
 placeholder="Search username, IP, or Caller ID..."
 value={searchQuery}
 onChange={(e) => {
 setSearchQuery(e.target.value);
 setCurrentPage(1);
 }}
 className="w-full px-4 py-2 pl-10 text-sm border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-700 dark:text-white"
 />
 <svg
 className="absolute left-3 top-2.5 w-4 h-4 text-gray-400"
 fill="none"
 viewBox="0 0 24 24"
 stroke="currentColor"
 >
 <path
 strokeLinecap="round"
 strokeLinejoin="round"
 strokeWidth={2}
 d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
 />
 </svg>
 </div>
 </div>
 </div>

 {/* Sessions Table */}
 {activeSessions && (() => {
 const filtered = getFilteredSessions();
 
 return (
 <>
 <div className="overflow-x-auto">
 {activeTab === 'pppoe' && (
 <table className="w-full text-sm">
 <thead className="bg-gray-50 dark:bg-slate-700 border-b border-gray-200 dark:border-slate-600">
 <tr>
 <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">Username</th>
 <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">IP Address</th>
 <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">Uptime</th>
 <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">Encoding</th>
 <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">Caller ID</th>
 </tr>
 </thead>
 <tbody>
 {filtered.data.length === 0 ? (
 <tr>
 <td colSpan="5" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
 <Wifi className="w-12 h-12 mx-auto mb-2 text-gray-300" />
 {searchQuery ? 'Tidak ada hasil yang cocok' : 'Tidak ada PPPoE session aktif'}
 </td>
 </tr>
 ) : (
 filtered.data.map((session, index) => (
 <tr key={session.id} className={index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-700'}>
 <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{session.name}</td>
 <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-mono text-xs">{session.address}</td>
 <td className="px-4 py-3 text-gray-700 dark:text-gray-200">{session.uptime}</td>
 <td className="px-4 py-3 text-gray-700 dark:text-gray-200">{session.encoding}</td>
 <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-mono text-xs">{session.caller_id}</td>
 </tr>
 ))
 )}
 </tbody>
 </table>
 )}

 {activeTab === 'hotspot' && (
 <table className="w-full text-sm">
 <thead className="bg-gray-50 dark:bg-slate-700 border-b border-gray-200 dark:border-slate-600">
 <tr>
 <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">User</th>
 <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">IP Address</th>
 <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">MAC Address</th>
 <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">Uptime</th>
 </tr>
 </thead>
 <tbody>
 {filtered.data.length === 0 ? (
 <tr>
 <td colSpan="4" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
 <Wifi className="w-12 h-12 mx-auto mb-2 text-gray-300" />
 {searchQuery ? 'Tidak ada hasil yang cocok' : 'Tidak ada Hotspot session aktif'}
 </td>
 </tr>
 ) : (
 filtered.data.map((session, index) => (
 <tr key={session.id} className={index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-700'}>
 <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{session.user}</td>
 <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-mono text-xs">{session.address}</td>
 <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-mono text-xs">{session.mac_address}</td>
 <td className="px-4 py-3 text-gray-700 dark:text-gray-200">{session.uptime}</td>
 </tr>
 ))
 )}
 </tbody>
 </table>
 )}
 </div>

 {/* Pagination Info & Controls */}
 <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-slate-700">
 <div className="text-sm text-gray-600 dark:text-gray-300">
 Showing {filtered.startIndex} to {filtered.endIndex} of {filtered.total} entries
 {searchQuery && <span className="ml-1">(filtered from {activeTab === 'pppoe' ? activeSessions.pppoe_sessions?.length : activeSessions.hotspot_sessions?.length})</span>}
 </div>
 
 {filtered.totalPages > 1 && (
 <div className="flex items-center gap-2">
 <button
 onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
 disabled={currentPage === 1}
 className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-slate-600 dark:hover:bg-slate-700/50"
 >
 Previous
 </button>
 
 <div className="flex items-center gap-1">
 {[...Array(filtered.totalPages)].map((_, i) => {
 const page = i + 1;
 // Show first, last, current, and pages around current
 if (
 page === 1 ||
 page === filtered.totalPages ||
 (page >= currentPage - 1 && page <= currentPage + 1)
 ) {
 return (
 <button
 key={page}
 onClick={() => setCurrentPage(page)}
 className={`px-3 py-1 text-sm border rounded-md ${
 currentPage === page
 ? 'bg-blue-500 text-white border-blue-500'
 : 'border-gray-300 hover:bg-gray-50'
 }`}
 >
 {page}
 </button>
 );
 } else if (page === currentPage - 2 || page === currentPage + 2) {
 return <span key={page} className="px-2 text-gray-400">...</span>;
 }
 return null;
 })}
 </div>
 
 <button
 onClick={() => setCurrentPage(prev => Math.min(filtered.totalPages, prev + 1))}
 disabled={currentPage === filtered.totalPages}
 className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-slate-600 dark:hover:bg-slate-700/50"
 >
 Next
 </button>
 </div>
 )}
 </div>
 </>
 );
 })()}
 </div>
 )}

 {/* Network Info Grid - hidden when offline */}
 {/* Network Interfaces & IP Pools - Grid Container */}
 {connectionStatus !== 'offline' && (
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
 {/* Network Interfaces - Table Format */}
 <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
 <div className="flex items-center gap-2 mb-4">
 <Network className="w-5 h-5 text-teal-600 dark:text-teal-400" />
 <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Network Interfaces</h2>
 </div>

 {/* Table Controls */}
 <div className="flex items-center justify-between mb-4 gap-4">
 <div className="flex items-center gap-2">
 <label className="text-sm text-gray-600 dark:text-gray-300">Show</label>
 <select
 value={interfacesEntriesPerPage}
 onChange={(e) => {
 setInterfacesEntriesPerPage(Number(e.target.value));
 setInterfacesCurrentPage(1);
 }}
 className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-slate-600"
 >
 <option value={10}>10</option>
 <option value={25}>25</option>
 <option value={50}>50</option>
 <option value={100}>100</option>
 </select>
 <label className="text-sm text-gray-600 dark:text-gray-300">entries</label>
 </div>

 <div className="flex-1 max-w-md">
 <div className="relative">
 <input
 type="text"
 placeholder="Search interface name, MAC..."
 value={interfacesSearchQuery}
 onChange={(e) => {
 setInterfacesSearchQuery(e.target.value);
 setInterfacesCurrentPage(1);
 }}
 className="w-full px-4 py-2 pl-10 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-slate-600"
 />
 <svg
 className="absolute left-3 top-2.5 w-4 h-4 text-gray-400"
 fill="none"
 viewBox="0 0 24 24"
 stroke="currentColor"
 >
 <path
 strokeLinecap="round"
 strokeLinejoin="round"
 strokeWidth={2}
 d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
 />
 </svg>
 </div>
 </div>
 </div>

 {/* Table */}
 {(() => {
 const filtered = getFilteredInterfaces();
 
 return (
 <>
 <div className="overflow-x-auto">
 <table className="w-full text-sm">
 <thead className="bg-gray-50 dark:bg-slate-700 border-b border-gray-200 dark:border-slate-600">
 <tr>
 <th onClick={() => toggleSort(interfacesSortField, interfacesSortDir, setInterfacesSortField, setInterfacesSortDir, 'name')} className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-600 select-none">Interface{sortIcon(interfacesSortField==='name', interfacesSortDir)}</th>
 <th onClick={() => toggleSort(interfacesSortField, interfacesSortDir, setInterfacesSortField, setInterfacesSortDir, 'mac_address')} className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-600 select-none">MAC Address{sortIcon(interfacesSortField==='mac_address', interfacesSortDir)}</th>
 <th onClick={() => toggleSort(interfacesSortField, interfacesSortDir, setInterfacesSortField, setInterfacesSortDir, 'status')} className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-600 select-none">Status{sortIcon(interfacesSortField==='status', interfacesSortDir)}</th>
 <th onClick={() => toggleSort(interfacesSortField, interfacesSortDir, setInterfacesSortField, setInterfacesSortDir, 'rx_byte')} className="px-4 py-2 text-right text-xs font-medium text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-600 select-none">Download{sortIcon(interfacesSortField==='rx_byte', interfacesSortDir)}</th>
 <th onClick={() => toggleSort(interfacesSortField, interfacesSortDir, setInterfacesSortField, setInterfacesSortDir, 'tx_byte')} className="px-4 py-2 text-right text-xs font-medium text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-600 select-none">Upload{sortIcon(interfacesSortField==='tx_byte', interfacesSortDir)}</th>
 </tr>
 </thead>
 <tbody>
 {filtered.data.length === 0 ? (
 <tr>
 <td colSpan="5" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
 <Network className="w-12 h-12 mx-auto mb-2 text-gray-300" />
 {interfacesSearchQuery ? 'Tidak ada hasil yang cocok' : 'No interfaces data'}
 </td>
 </tr>
 ) : (
 filtered.data.map((iface, index) => (
 <tr key={index} className={index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-700'}>
 <td className="px-4 py-3">
 <div className="font-medium text-gray-900 dark:text-white">{iface.name}</div>
 {iface.comment && (
 <div className="text-xs text-gray-400 dark:text-gray-500">{iface.comment}</div>
 )}
 </td>
 <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-mono text-xs">{iface.mac_address}</td>
 <td className="px-4 py-3">
 {iface.running && !iface.disabled ? (
 <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-400">
 <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
 Running
 </span>
 ) : iface.disabled ? (
 <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-400">
 <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
 Disabled
 </span>
 ) : (
 <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:text-gray-100 dark:bg-slate-800">
 Stopped
 </span>
 )}
 </td>
 <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300 font-mono text-xs">
 {formatBytes(iface.rx_byte)}
 </td>
 <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300 font-mono text-xs">
 {formatBytes(iface.tx_byte)}
 </td>
 </tr>
 ))
 )}
 </tbody>
 </table>
 </div>

 {/* Pagination */}
 <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-slate-700">
 <div className="text-sm text-gray-600 dark:text-gray-300">
 Showing {filtered.startIndex} to {filtered.endIndex} of {filtered.total} entries
 {interfacesSearchQuery && <span className="ml-1">(filtered from {interfaces.length})</span>}
 </div>
 
 {filtered.totalPages > 1 && (
 <div className="flex items-center gap-2">
 <button
 onClick={() => setInterfacesCurrentPage(prev => Math.max(1, prev - 1))}
 disabled={interfacesCurrentPage === 1}
 className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-slate-600 dark:hover:bg-slate-700/50"
 >
 Previous
 </button>
 
 <div className="flex items-center gap-1">
 {[...Array(filtered.totalPages)].map((_, i) => {
 const page = i + 1;
 if (
 page === 1 ||
 page === filtered.totalPages ||
 (page >= interfacesCurrentPage - 1 && page <= interfacesCurrentPage + 1)
 ) {
 return (
 <button
 key={page}
 onClick={() => setInterfacesCurrentPage(page)}
 className={`px-3 py-1 text-sm border rounded-md ${
 interfacesCurrentPage === page
 ? 'bg-teal-500 text-white border-teal-500'
 : 'border-gray-300 hover:bg-gray-50'
 }`}
 >
 {page}
 </button>
 );
 } else if (page === interfacesCurrentPage - 2 || page === interfacesCurrentPage + 2) {
 return <span key={page} className="px-2 text-gray-400">...</span>;
 }
 return null;
 })}
 </div>
 
 <button
 onClick={() => setInterfacesCurrentPage(prev => Math.min(filtered.totalPages, prev + 1))}
 disabled={interfacesCurrentPage === filtered.totalPages}
 className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-slate-600 dark:hover:bg-slate-700/50"
 >
 Next
 </button>
 </div>
 )}
 </div>
 </>
 );
 })()}
 </div>

 {/* IP Pools - Table Format */}
 <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
 <div className="flex items-center gap-2 mb-4">
 <Signal className="w-5 h-5 text-amber-600 dark:text-amber-400" />
 <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Simple Queues</h2>
 </div>

 {/* Table Controls */}
 <div className="flex items-center justify-between mb-4 gap-4">
 <div className="flex items-center gap-2">
 <label className="text-sm text-gray-600 dark:text-gray-300">Show</label>
 <select
 value={poolsEntriesPerPage}
 onChange={(e) => {
 setPoolsEntriesPerPage(Number(e.target.value));
 setPoolsCurrentPage(1);
 }}
 className="px-3 py-1.5 text-sm border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white dark:bg-slate-700 dark:text-white"
 >
 <option value={10}>10</option>
 <option value={25}>25</option>
 <option value={50}>50</option>
 <option value={100}>100</option>
 </select>
 <label className="text-sm text-gray-600 dark:text-gray-300">entries</label>
 </div>

 <div className="flex-1 max-w-md">
 <div className="relative">
 <input
 type="text"
 placeholder="Search queue name, target, limit..."
 value={poolsSearchQuery}
 onChange={(e) => {
 setPoolsSearchQuery(e.target.value);
 setPoolsCurrentPage(1);
 }}
 className="w-full px-4 py-2 pl-10 text-sm border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white dark:bg-slate-700 dark:text-white"
 />
 <svg
 className="absolute left-3 top-2.5 w-4 h-4 text-gray-400"
 fill="none"
 viewBox="0 0 24 24"
 stroke="currentColor"
 >
 <path
 strokeLinecap="round"
 strokeLinejoin="round"
 strokeWidth={2}
 d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
 />
 </svg>
 </div>
 </div>
 </div>

 {/* Table */}
 {(() => {
 const filtered = getFilteredPools();
 
 return (
 <>
 <div className="overflow-x-auto">
 <table className="w-full text-sm">
 <thead className="bg-gray-50 dark:bg-slate-700 border-b border-gray-200 dark:border-slate-600">
 <tr>
 <th onClick={() => toggleSort(poolsSortField, poolsSortDir, setPoolsSortField, setPoolsSortDir, 'name')} className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-600 select-none">Name{sortIcon(poolsSortField==='name', poolsSortDir)}</th>
 <th onClick={() => toggleSort(poolsSortField, poolsSortDir, setPoolsSortField, setPoolsSortDir, 'target')} className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-600 select-none">Target{sortIcon(poolsSortField==='target', poolsSortDir)}</th>
 <th onClick={() => toggleSort(poolsSortField, poolsSortDir, setPoolsSortField, setPoolsSortDir, 'max_limit')} className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-600 select-none">Max Limit (Up/Down){sortIcon(poolsSortField==='max_limit', poolsSortDir)}</th>
 <th onClick={() => toggleSort(poolsSortField, poolsSortDir, setPoolsSortField, setPoolsSortDir, 'parent')} className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-600 select-none">Parent{sortIcon(poolsSortField==='parent', poolsSortDir)}</th>
 </tr>
 </thead>
 <tbody>
 {filtered.data.length === 0 ? (
 <tr>
 <td colSpan="4" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
 <Signal className="w-12 h-12 mx-auto mb-2 text-gray-300" />
 {poolsSearchQuery ? 'Tidak ada hasil yang cocok' : 'No Simple Queues configured'}
 </td>
 </tr>
 ) : (
 filtered.data.map((q, index) => (
 <tr key={index} className={index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-700'}>
 <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
 {q.disabled && <span className="inline-block w-2 h-2 rounded-full bg-red-400 mr-2" title="Disabled"></span>}
 {q.name}
 </td>
 <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-mono text-xs">{q.target || '-'}</td>
 <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-mono text-xs">{q.max_limit || '-'}</td>
 <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">{q.parent || 'none'}</td>
 </tr>
 ))
 )}
 </tbody>
 </table>
 </div>

 {/* Pagination */}
 <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-slate-700">
 <div className="text-sm text-gray-600 dark:text-gray-300">
 Showing {filtered.startIndex} to {filtered.endIndex} of {filtered.total} entries
 {poolsSearchQuery && <span className="ml-1">(filtered from {simpleQueues.length})</span>}
 </div>
 
 {filtered.totalPages > 1 && (
 <div className="flex items-center gap-2">
 <button
 onClick={() => setPoolsCurrentPage(prev => Math.max(1, prev - 1))}
 disabled={poolsCurrentPage === 1}
 className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-slate-600 dark:hover:bg-slate-700/50"
 >
 Previous
 </button>
 
 <div className="flex items-center gap-1">
 {[...Array(filtered.totalPages)].map((_, i) => {
 const page = i + 1;
 if (
 page === 1 ||
 page === filtered.totalPages ||
 (page >= poolsCurrentPage - 1 && page <= poolsCurrentPage + 1)
 ) {
 return (
 <button
 key={page}
 onClick={() => setPoolsCurrentPage(page)}
 className={`px-3 py-1 text-sm border rounded-md ${
 poolsCurrentPage === page
 ? 'bg-amber-500 text-white border-amber-500'
 : 'border-gray-300 hover:bg-gray-50'
 }`}
 >
 {page}
 </button>
 );
 } else if (page === poolsCurrentPage - 2 || page === poolsCurrentPage + 2) {
 return <span key={page} className="px-2 text-gray-400">...</span>;
 }
 return null;
 })}
 </div>
 
 <button
 onClick={() => setPoolsCurrentPage(prev => Math.min(filtered.totalPages, prev + 1))}
 disabled={poolsCurrentPage === filtered.totalPages}
 className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-slate-600 dark:hover:bg-slate-700/50"
 >
 Next
 </button>
 </div>
 )}
 </div>
 </>
 );
 })()}
 </div>
 </div>
 )}
 
 {/* Custom Confirm Modal */}
 {confirmModal.isOpen && (
 <div className="fixed inset-0 z-50 flex items-center justify-center">
 {/* Backdrop */}
 <div 
 className="absolute inset-0 bg-black/60 backdrop-blur-sm"
 onClick={confirmModal.onCancel}
 />
 
 {/* Modal */}
 <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden transform transition-all animate-in fade-in zoom-in duration-200 dark:bg-slate-800">
 {/* Header with gradient based on type */}
 <div className={`px-6 py-4 ${
 confirmModal.type === 'danger' 
 ? 'bg-gradient-to-r from-red-500 to-red-600' 
 : confirmModal.type === 'warning'
 ? 'bg-gradient-to-r from-amber-500 to-orange-500'
 : 'bg-gradient-to-r from-blue-500 to-blue-600'
 }`}>
 <div className="flex items-center gap-3">
 {confirmModal.type === 'danger' && (
 <div className="p-2 bg-white/20 rounded-full">
 <AlertTriangle className="h-6 w-6 text-white" />
 </div>
 )}
 {confirmModal.type === 'warning' && (
 <div className="p-2 bg-white/20 rounded-full">
 <AlertTriangle className="h-6 w-6 text-white" />
 </div>
 )}
 <h3 className="text-lg font-bold text-white">{confirmModal.title}</h3>
 </div>
 </div>
 
 {/* Content */}
 <div className="px-6 py-5">
 <p className="text-gray-700 whitespace-pre-line leading-relaxed dark:text-slate-200">
 {confirmModal.message}
 </p>
 </div>
 
 {/* Actions */}
 <div className="px-6 py-4 bg-gray-50 flex gap-3 justify-end dark:bg-slate-900">
 {confirmModal.showCancel && (
 <button
 onClick={confirmModal.onCancel}
 className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors dark:text-slate-200 dark:bg-slate-800 dark:border-slate-600 dark:hover:bg-slate-700/50"
 >
 {confirmModal.cancelText}
 </button>
 )}
 <button
 onClick={confirmModal.onConfirm}
 className={`px-5 py-2.5 text-sm font-medium text-white rounded-lg transition-colors ${
 confirmModal.type === 'danger'
 ? 'bg-red-600 hover:bg-red-700'
 : confirmModal.type === 'warning'
 ? 'bg-amber-600 hover:bg-amber-700'
 : 'bg-blue-600 hover:bg-blue-700'
 }`}
 >
 {confirmModal.confirmText}
 </button>
 </div>
 </div>
 </div>
 )}
 
 {/* Custom Alert Modal */}
 {alertModal.isOpen && (
 <div className="fixed inset-0 z-50 flex items-center justify-center">
 {/* Backdrop */}
 <div 
 className="absolute inset-0 bg-black/60 backdrop-blur-sm"
 onClick={() => setAlertModal(prev => ({ ...prev, isOpen: false }))}
 />
 
 {/* Modal */}
 <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden transform transition-all animate-in fade-in zoom-in duration-200 dark:bg-slate-800">
 {/* Header with gradient based on type */}
 <div className={`px-6 py-4 ${
 alertModal.type === 'error' 
 ? 'bg-gradient-to-r from-red-500 to-red-600' 
 : alertModal.type === 'success'
 ? 'bg-gradient-to-r from-green-500 to-emerald-600'
 : 'bg-gradient-to-r from-blue-500 to-blue-600'
 }`}>
 <div className="flex items-center gap-3">
 {alertModal.type === 'error' && (
 <div className="p-2 bg-white/20 rounded-full">
 <XCircle className="h-6 w-6 text-white" />
 </div>
 )}
 {alertModal.type === 'success' && (
 <div className="p-2 bg-white/20 rounded-full">
 <CheckCircle className="h-6 w-6 text-white" />
 </div>
 )}
 {alertModal.type === 'info' && (
 <div className="p-2 bg-white/20 rounded-full">
 <AlertTriangle className="h-6 w-6 text-white" />
 </div>
 )}
 <h3 className="text-lg font-bold text-white">{alertModal.title}</h3>
 </div>
 </div>
 
 {/* Content */}
 <div className="px-6 py-5">
 <p className="text-gray-700 whitespace-pre-line leading-relaxed dark:text-slate-200">
 {alertModal.message}
 </p>
 </div>
 
 {/* Actions */}
 <div className="px-6 py-4 bg-gray-50 flex justify-end dark:bg-slate-900">
 <button
 onClick={() => setAlertModal(prev => ({ ...prev, isOpen: false }))}
 className={`px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-colors ${
 alertModal.type === 'error'
 ? 'bg-red-600 hover:bg-red-700'
 : alertModal.type === 'success'
 ? 'bg-green-600 hover:bg-green-700'
 : 'bg-blue-600 hover:bg-blue-700'
 }`}
 >
 OK
 </button>
 </div>
 </div>
 </div>
 )}
 </div>
 );
};

export default MPanel;
